import { React, useEffect, useState } from 'react'
import { Card } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { colors, breakpoints } from 'globalStyles'
import SVGIcon from 'reusableComponents/SVGIcon'
import { ReactComponent as Buzzer } from 'assets/svg/buzzer.svg'
import { ReactComponent as Pencil } from 'assets/svg/pencil.svg'
import PropTypes from 'prop-types'
import InputField from 'reusableComponents/InputField/InputField'
import { getUserPreferencesInfo } from 'store/recipient/recipientSlice'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Button from 'react-bootstrap/Button'
import SuccessModal from 'components/SuccessModal'
import { updateUserPreferences } from 'api/accounts'
import CheckMark from 'reusableComponents/CheckMark'
import {
  pencilIconStyle,
  tableHeaderStyle,
  tableColumnStyle,
  tableContentStyle,
  tableSubContentStyle,
  buzzerIconStyle,
  StyledCard,
} from './MyPreferences.style'

const pywBackendURL = process.env.REACT_APP_PYW_BACKEND_API

const StyledCardTitle = styled(Card.Title)`
  text-align: left;
  width: 98%;

  @media ${breakpoints.phone} {
    text-align: center;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;
  width: 98%;
  box-sizing: border-box;

  button {
    flex: 1;
  }
`

const BuzzerCodePreference = ({ userPreferenceDetails }) => {
  const prn = pywBackendURL
  const { t, i18n } = useTranslation()
  const { lang } = useParams()
  const dispatch = useDispatch()
  const { language } = i18n
  const [disableSaveButton, setDisableSaveButton] = useState(true)
  const [isEditingBuzzerCodePreference, setIsEditingBuzzerCodePreference] = useState(false)
  const [showGreenCheck, setShowGreenCheck] = useState(false)
  const [buzzerCodePreference, setBuzzerCodePreference] = useState(userPreferenceDetails.buzzerCode)
  const [saveBuzzerCodePreference, setSaveBuzzerCodePreference] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const buzzerCode = buzzerCodePreference || t(`my_preferences.none_selected`)

  const onClickCancel = () => {
    setBuzzerCodePreference(userPreferenceDetails.buzzerCode || '')
    setDisableSaveButton(true)
    setIsEditingBuzzerCodePreference(false)
  }

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
    if (saveBuzzerCodePreference) dispatch(getUserPreferencesInfo(prn))
  }, [lang, i18n, language, saveBuzzerCodePreference, dispatch, prn])

  const editBuzzerCodePreference = () => {
    setBuzzerCodePreference(userPreferenceDetails.buzzerCode || '')
    setDisableSaveButton(true)
    setIsEditingBuzzerCodePreference(true)
  }

  const buzzerCodeChangeHandler = (event) => {
    const selectedBuzzerCode = event.target.value
    setBuzzerCodePreference(selectedBuzzerCode)

    const existingCode = (userPreferenceDetails?.buzzerCode || '').trim()
    // Allow saving an empty value only when removing an existing code
    setDisableSaveButton(
      selectedBuzzerCode.length === 0
        ? existingCode.length === 0
        : selectedBuzzerCode.trim() === existingCode
    )
  }

  const cancelModal = () => {
    setShowModal(false)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    const hadExistingBuzzerCode = Boolean((userPreferenceDetails?.buzzerCode || '').trim())
    const messageKey = hadExistingBuzzerCode
      ? 'my_preferences.successfully_updated_buzzCode'
      : 'my_preferences.successfully_added_buzzCode'
    const message = t(messageKey)
    try {
      await updateUserPreferences('buzzerCode', buzzerCodePreference)
    } catch (err) {
      console.error('Failed to update user preferences.', err)
    } finally {
      setSaveBuzzerCodePreference(true)
      setSuccessMessage(message)
      setShowModal(true)
      setTimeout(() => {
        setShowModal(false)
        setShowGreenCheck(true)
      }, 3000)

      setIsEditingBuzzerCodePreference(false)
      setDisableSaveButton(true)
    }
  }
  const handleCheckMarkComplete = () => {
    setShowGreenCheck(false)
  }
  return (
    <StyledCard style={{ marginTop: '24px' }}>
      <Card.Header
        style={{
          backgroundColor: isEditingBuzzerCodePreference ? `${colors.sky_blue}` : 'inherit',
        }}
      >
        <div style={{ display: 'table', width: '100%' }}>
          <div style={tableHeaderStyle}>
            <SVGIcon
              Icon={Buzzer}
              size="lg"
              aria-label="Buzzer code preference"
              style={buzzerIconStyle}
            />
          </div>
          <div style={{ display: 'table-cell' }}>
            <div style={tableContentStyle}>{t(`my_preferences.add_buzz_code`)}</div>
            <div style={tableSubContentStyle}>{buzzerCode}</div>
          </div>
          <div style={tableColumnStyle}>
            {!isEditingBuzzerCodePreference && !showGreenCheck && (
              <SVGIcon
                Icon={Pencil}
                size="lg"
                color={colors.primary_blue}
                aria-label="Edit buzzer code preference"
                onClick={editBuzzerCodePreference}
                style={pencilIconStyle}
              />
            )}
            {!isEditingBuzzerCodePreference && showGreenCheck && (
              <CheckMark
                color={colors.primary_green}
                size="lg"
                delay="10000"
                onComplete={handleCheckMarkComplete}
              />
            )}
          </div>
        </div>
      </Card.Header>
      <Form id="saveBuzzerCodeForm" onSubmit={handleSubmit}>
        {isEditingBuzzerCodePreference && (
          <Card.Body>
            <StyledCardTitle>{t(`my_preferences.buzz_code`)}</StyledCardTitle>
            <FloatingLabel id="buzzerCode">
              <InputField
                type="text"
                value={buzzerCodePreference || ''}
                label={t('my_preferences.add_buzz_code')}
                onChange={buzzerCodeChangeHandler}
                style={{ width: '98%' }}
                id="buzzCodeInput"
                key="buzzCode"
                maxLength={6}
              />
            </FloatingLabel>
            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={onClickCancel}>
                {t('buttonActions.cancel')}
              </Button>
              <Button type="submit" form="saveBuzzerCodeForm" disabled={disableSaveButton}>
                {t('buttonActions.save')}
              </Button>
            </ButtonContainer>
          </Card.Body>
        )}
        <SuccessModal show={showModal} onHide={cancelModal} message={successMessage} />
      </Form>
    </StyledCard>
  )
}

BuzzerCodePreference.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  userPreferenceDetails: PropTypes.object.isRequired,
}

export default BuzzerCodePreference
